import React, { useMemo } from 'react';
import { TrackingEvent } from '../../Services/Tracking/TrackingService';
import TreeItem, { ITreeNode } from './TreeItem';

export interface ITreeViewProps {
    data: ITreeNode[];
    searchTerm: string;
    handleSelect?: (event: React.MouseEvent<HTMLElement>) => void;
    handleExpand?: (event: React.MouseEvent<HTMLElement>) => void;
    disabledNodes?: (ITreeNode | null | undefined)[];
    useCheckboxSelection?: boolean;
    noData?: React.ReactNode;
    track: TrackingEvent;
}

const TreeView: React.FC<ITreeViewProps> = (props: ITreeViewProps) => {
    const isNodeDisabled = (node: ITreeNode) => {
        if (props.disabledNodes) {
            return props.disabledNodes.some((disabledNode) => disabledNode && disabledNode.id === node.id);
        }

        return false;
    };

    const expandNodesMatchingSearch = (nodes: ITreeNode[], searchTerm: string): ITreeNode[] =>
        nodes.reduce<ITreeNode[]>((acc, node) => {
            const matchesSearch = node.name.toLowerCase().includes(searchTerm.toLowerCase());
            const children = node.children.length ? expandNodesMatchingSearch(node.children, searchTerm) : [];

            if (matchesSearch || children.length > 0) {
                acc.push({
                    ...node,
                    expanded: true,
                    children,
                });
            }

            return acc;
        }, []);

    const getFilteredData = useMemo(() => {
        if (props.searchTerm) {
            return expandNodesMatchingSearch(props.data, props.searchTerm);
        }

        return props.data;
    }, [props.searchTerm, props.data]);

    const setSelectedNode = (event: React.MouseEvent<HTMLElement>) => {
        if (props.handleSelect) {
            props.handleSelect(event);
        }
    };

    const setExpandedNode = (event: React.MouseEvent<HTMLElement>) => {
        if (props.handleExpand) {
            props.handleExpand(event);
        }
    };

    return (
        <div>
            {getFilteredData &&
                getFilteredData.map((node) => (
                    <TreeItem
                        key={node.id}
                        node={node}
                        searchTerm={props.searchTerm}
                        setSelectedNode={setSelectedNode}
                        setExpandedNode={setExpandedNode}
                        disabled={isNodeDisabled(node)}
                        disabledNodes={props.disabledNodes}
                        expanded={node.expanded || false}
                        level={0}
                        useCheckboxSelection={props.useCheckboxSelection || false}
                        track={{ eventCategory: props.track.eventCategory, eventAction: props.track.eventAction, eventValue: node.name }}
                    />
                ))}
            {!getFilteredData?.length && props.noData}
        </div>
    );
};

export default TreeView;
